import {
  ArchivedToggle,
  AttributionDialog,
  ExpenseFilters,
  ExpensesChart,
  ExpenseForm,
  ExpenseList,
  ExpenseStats,
  PaymentScheduleSection,
  useExpenses,
  useExpensesSchedule,
  type ExpenseFilterState,
  type ExpenseRowDto,
} from "@gladis/domain-expenses/ui";
import { useApiQuery } from "@gladis/api-client";
import type { CurrentUserDto, MemberDto } from "@gladis/shared";
import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/expenses")({
  component: Expenses,
});

/**
 * Expenses.
 *
 * Composition only — the list and the form come from the expenses package, which owns what a recurring
 * expense is. This route decides where they appear.
 *
 * ALL SEVEN STORIES ARE HERE. The mortgage-interest section reads its own data from the same query key the
 * list uses — TanStack Query dedupes it, so it is not a second request, and it cannot disagree with the totals
 * beside it about which response it is showing.
 *
 * CARDS ARE NOT SET UP HERE. They have their own page (`/cards`), and this page keeps only what it needs from
 * them: the picker on the form, the card named on each row, and the settle-up derived from card ownership. A
 * card is created once and read for years; an expense is entered every month. Putting the once-a-year task on
 * the monthly page made the monthly page longer for no monthly benefit.
 *
 * THE FILTER STATE LIVES HERE, not in the list. The list is given what to show; deciding what that is belongs
 * to the page, which is also where the card filter will join in US4 without the list changing at all.
 *
 * THE MEMBER LIST IS FETCHED HERE, for the split editor only.
 *
 * The server never trusts it: `createExpense` and `editExpense` are handed identity's answer by the composition
 * root and validate every share against that. This copy exists so the form can OFFER the right names, and
 * offering the wrong ones would be refused rather than accepted — which is the difference between a convenience
 * and a source of truth.
 */
function Expenses(): ReactNode {
  const t = useT();
  const [adding, setAdding] = useState(false);
  /*
   * WHICH expense the form is editing, or null for a new one.
   *
   * The row itself rather than an id: the form fills its fields from it, and holding an id would mean looking
   * the row up again from a payload that may have refetched in between — which is how a form comes to show a
   * stale amount.
   */
  const [editing, setEditing] = useState<ExpenseRowDto | null>(null);
  /** Which mortgage row's attribution is open, or null. A mortgage row cannot be edited, only attributed. */
  const [attributing, setAttributing] = useState<ExpenseRowDto | null>(null);
  /*
   * ONE FILTER STATE FOR THE PAGE, and it holds two different kinds of thing on purpose.
   *
   * `member` is the page's filter and lives in the title row, where the dashboard's do. It governs the
   * trend AND the list, which is what makes it a page-level control rather than a section's.
   *
   * `state` is the archived VIEW SWITCH and lives in the list's own header, because it means nothing to
   * a chart that already reconstructs what was live in each month. One state object rather than two:
   * they are read together by `useExpenses`, and splitting them would mean recombining them at the call.
   *
   * There were briefly two states, one per section, driving four controls each. Category and card have
   * gone entirely — see `ExpenseFilters`.
   *
   * THE STAT CARDS FOLLOW `member` TOO, and used not to. They answered the household's question beside a
   * table and a chart answering somebody else's, which is the defect this page was reported for: four
   * sections, one control, four different subjects. `member` scopes every figure on the page now — the
   * server returns the member's SHARE, by the one rule in `scoped-share.ts` — and each section says whose
   * figures it is showing. `state` still governs the list alone, because an archived row belongs to no
   * total and a chart already reconstructs what was live in each month.
   */
  const [filters, setFilters] = useState<ExpenseFilterState>({});
  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;

  /*
   * The member list, fetched only once there is a household to ask about — the same shape the Savings page
   * uses, and identity's answer rather than anything this module derives.
   *
   * The server validates every share against its own copy on submit, so this is a convenience for offering
   * the right names and not the enforcement. Offering a wrong one is refused, not accepted.
   */
  /*
   * The page's own data, read here as well as inside `ExpenseList`.
   *
   * TanStack Query dedupes it — same key, one request — so this costs nothing, and it is what lets the form's
   * card picker and the filters offer the household's cards without either component fetching for the other.
   */
  const page = useExpenses(filters);
  const cards = page.data?.cards ?? [];

  /*
   * THE SCHEDULE'S ANCHORS AND NEXT DUE DATES (feature 024) — period-independent, so the month read serves
   * them; it is the read the section opens on, so it is usually the same cache entry rather than a second
   * request. They are not on the page read, whose budget of 13 does not move.
   */
  const schedule = useExpensesSchedule("month", filters.member);
  const anchorOf = (row: ExpenseRowDto | null): string | null => {
    if (row === null) return null;
    const key =
      row.kind === "derived" && row.sourceId !== null ? `mortgage:${row.sourceId}` : row.id;
    return schedule.data?.currentAnchor[key] ?? null;
  };

  /** The row behind a schedule key — a leaf, or a vehicle's cost inside its group. */
  const rowFor = (key: string): ExpenseRowDto | null => {
    for (const row of page.data?.rows ?? []) {
      const candidates = row.kind === "group" ? row.children : [row];
      for (const candidate of candidates) {
        const candidateKey =
          candidate.kind === "derived" && candidate.sourceId !== null
            ? `mortgage:${candidate.sourceId}`
            : candidate.id;
        if (candidateKey === key) return candidate;
      }
    }
    return null;
  };

  const members = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );

  return (
    <Page>
      {/*
       * ADDING AN EXPENSE IS THIS PAGE'S PRIMARY ACTION, so it goes where `Page.Title` puts every
       * page's: the end of the title row. It used to live in `ExpenseList`'s own header, which put the
       * thing a member came to do below a settle-up panel, a filter row and a chart — three sections
       * deep on the page they open every month.
       */}
      <Page.Title
        actions={
          <>
            {/*
              THE PAGE'S FILTER, BESIDE ITS PRIMARY ACTION. `Page.Title`'s slot is "a row", and its own
              comment says a page may pair a secondary control with its primary one — this is that pair,
              and it is the arrangement the Dashboard already uses for the same kind of control.
            */}
            <ExpenseFilters
              value={filters}
              members={members.data?.members ?? []}
              onChange={setFilters}
            />
            <Button
              variant="primary"
              // Opens rather than toggles: the form is a modal with three ways to close and this control is
              // not one of them — while it is open this button is behind it and inert. `aria-haspopup`
              // rather than the `aria-expanded` a toggle would carry.
              aria-haspopup="dialog"
              onClick={() => {
                setAdding(true);
              }}
            >
              {t("page.expenses.addExpense")}
            </Button>
          </>
        }
      >
        {t("nav.expenses")}
      </Page.Title>
      <Page.Body>
        <div className="col-span-full flex flex-col gap-6">
          {/* Always mounted; `open` decides whether it is seen. See the note on `CardForm` in
              `cards.tsx` for why a native `<dialog>` cannot be conditionally rendered. */}
          <ExpenseForm
            open={adding || editing !== null}
            onClose={() => {
              setAdding(false);
              setEditing(null);
            }}
            editing={editing}
            currentAnchor={anchorOf(editing)}
            members={members.data?.members ?? []}
            cards={cards}
            onCreated={() => {
              setAdding(false);
              setEditing(null);
            }}
          />

          {/*
            THE FIGURES, FIRST — what this comes to per month, whether it is moving, whose it is, and who
            owes whom.

            The settle-up is one of them, and it is still ABOVE the list (FR-065): it is the answer the page
            exists to give, not a footnote to it. It used to be a panel of its own here; as a card it sits
            beside the three figures a reader compares it against, which is where it was always being read.

            Given the payload rather than fetching it — see `ExpenseStats`. It IS told the filters: the
            payload arrives already scoped by them, and the component needs to know which of the three
            scopes produced it so each card can say so.
          */}
          <ExpenseStats
            page={page.data}
            filters={filters}
            householdMembers={members.data?.members ?? []}
          />

          {/*
            THE TREND, above the list and below the settle-up.

            It answers a different question from everything else on the page — not "what do we pay" but "is
            that changing" — and it goes above the list because a member scanning nine rows for a rise is
            doing by hand what the line does at a glance. It renders nothing at all for a household that has
            recorded nothing.
          */}
          {/* `members` only so the hint can name whose line it is drawing. */}
          {/*
            WHEN THE MONEY LEAVES (feature 024), between the figures and the trend: it changes daily where
            the trend changes monthly. Governed by the page's "whose cost" filter and by nothing else — the
            archived toggle and the search belong to the list (FR-033).
          */}
          <PaymentScheduleSection
            {...(filters.member === undefined ? {} : { member: filters.member })}
            members={members.data?.members ?? []}
            onSetDate={(key) => {
              const row = rowFor(key);
              if (row === null) return;
              // A mortgage row is dated in "Who pays", every other row in the expense form (FR-023).
              if (row.kind === "derived") setAttributing(row);
              else setEditing(row);
            }}
          />

          <ExpensesChart filters={filters} members={members.data?.members ?? []} />

          <ExpenseList
            filters={filters}
            members={members.data?.members ?? []}
            // The archived view switch, in the header of the list it switches. Not a filter, and not
            // in the title row — see `ArchivedToggle`.
            controls={<ArchivedToggle value={filters} onChange={setFilters} />}
            onEdit={(row) => {
              setEditing(row);
            }}
            onAttribute={(row) => {
              setAttributing(row);
            }}
            nextDue={schedule.data?.nextDue}
          />

          {/*
            The attribution editor for a mortgage row — which card pays it, and whose cost it is.

            Rendered here rather than inside the list for the same reason the expense form is: the route owns
            where a dialog appears, and the list's job is to say which row was clicked.
          */}
          <AttributionDialog
            open={attributing !== null}
            onClose={() => {
              setAttributing(null);
            }}
            line={attributing}
            currentAnchor={anchorOf(attributing)}
            members={members.data?.members ?? []}
            cards={cards}
          />
        </div>
      </Page.Body>
    </Page>
  );
}
